<!--
/**
 * angular-admin-oskey-io
 *
 * @license
 * Copyright (c) 2021-2025, OSKEY SAS. All rights reserved.
 *
 * The source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 *
 * The source code is confidential and only available to authorized individuals with the permission of the copyright holders.
 * If you encounter this source code and do not have permission, please contact the copyright holders and delete this file.
 */
-->

<div class="sidenav oskey-sidenav osk-nav-bar-height" [class.sidenav-collapsed]="isCollapsed">
  @if (!isCollapsed) {
    @if (routerUrl.startsWith('/organization') || routerUrl.startsWith('/user/organizations')) {
      <div class="portal-header-card">
        <div class="box-entity">
          <div class="box-content">
            <img class="org-logo" src="assets/img/brand-logo.png" alt="Organization" />
            <div class="org-name">
              <p class="title">Les Estudines Paris</p>
              <p class="subtitle">{{ 'portals.sidemenu.subtitle' | oskTranslate | async }}</p>
            </div>
          </div>
        </div>
        @if (routerUrl.startsWith('/organization')) {
          <osk-button
            color="primary"
            appearance="solid"
            size="sm"
            leadingIcon="plus"
            [fullWidth]="true"
            class="addEntity"
          >
            {{ 'portals.sidemenu.button' | oskTranslate | async }}
          </osk-button>
        } @else if (routerUrl.startsWith('/user/organizations')) {
          <img class="org-picture" src="assets/img/photo1.png" alt="Property" />
          <div class="sidemenu-submenu-group">
            <button
              type="button"
              class="sidemenu-submenu-header"
              (click)="toggleSubmenuGroup()"
              [attr.aria-expanded]="submenuGroupOpen()"
            >
              <div class="header-container">
                <div class="submenu-panel-title">Asnières-sur-Seine</div>
                <div class="submenu-panel-subtitle">
                  {{ 'portals.sidemenu.panelSubtitle' | oskTranslate | async }}
                </div>
              </div>
              <osk-icon
                [name]="$any(submenuGroupOpen() ? 'chevron-down' : 'chevron-right')"
                [size]="16"
              ></osk-icon>
            </button>
            @if (submenuGroupOpen()) {
              <div class="sidemenu-submenu-content">République</div>
            }
          </div>
        }
      </div>
    } @else {
      <div class="portal-header-card">
        <div class="box-entity">
          <div class="box-content">
            <img class="org-logo" src="assets/img/oskey_rounded.png" alt="Oskey Admin" />
            <div class="org-name">
              <p class="title">Oskey Admin</p>
              <osk-badge color="primary" appearance="soft" size="sm" [pill]="true">
                Platform
              </osk-badge>
            </div>
          </div>
        </div>
        <div class="portal-header-accent"></div>
      </div>
    }
  } @else {
    <div class="collapsed-brand">
      <img src="assets/img/oskey_rounded.png" width="36" height="36" alt="Oskey" />
    </div>
  }

  @if (sidemenu() && sidemenu().menus.length > 0 && currentUser().selectedAccount) {
    <div class="scrollwrap scrollwrap_delayed">
      <ul class="nav-menu">
        @if (
          (sidemenu() && sidemenu().activeMenuIndex > 0) ||
          (sidemenu() && sidemenu().activeMenuIndex < sidemenu().menus.length - 1)
        ) {
          <div class="menu-step-nav">
            @if (sidemenu() && sidemenu().activeMenuIndex > 0) {
              <osk-button
                color="neutral"
                appearance="ghost"
                size="sm"
                leadingIcon="chevron-left"
                (clicked)="showPreviousMenu()"
              >
                @if (!isCollapsed) {
                  {{
                    sidemenu().menus[sidemenu().activeMenuIndex - 1].name ||
                      ('back' | oskTranslate | async)
                  }}
                }
              </osk-button>
            }
            @if (sidemenu() && sidemenu().activeMenuIndex < sidemenu().menus.length - 1) {
              <osk-button
                color="neutral"
                appearance="ghost"
                size="sm"
                [iconOnly]="true"
                leadingIcon="chevron-right"
                [ariaLabel]="('shared.nextMenu' | oskTranslate | async) ?? ''"
                (clicked)="showNextMenu()"
              />
            }
          </div>
        }

        @for (item of sidemenu().menus[sidemenu().activeMenuIndex].items; track item.routerLink) {
          @if (item.isDivider) {
            <li class="nav-divider">
              @if (item.label && !isCollapsed) {
                <span class="divider-label">{{ item.label | oskTranslate | async }}</span>
              } @else {
                <osk-divider />
              }
            </li>
          } @else {
            <li
              [routerLink]="currentUser().selectedAccount!.path + item.routerLink"
              routerLinkActive="active"
              [routerLinkActiveOptions]="{ exact: true }"
              #rla="routerLinkActive"
              class="nav-item"
            >
              <div class="nav-item-icon">
                @if (item.icon && item.icon.includes('.svg')) {
                  <div
                    class="svg-icon"
                    [style.mask-image]="
                      'url(' +
                      (rla.isActive ? item.icon.replace('-stroke.svg', '-fill.svg') : item.icon) +
                      ')'
                    "
                  ></div>
                } @else {
                  <osk-icon [name]="$any(item.icon)" [size]="18"></osk-icon>
                }
              </div>
              @if (!isCollapsed) {
                <span @fadeInOut class="osk-small-text">{{
                  item.label | oskTranslate | async
                }}</span>
              }
            </li>
          }
        }
      </ul>
    </div>
  }

  <div class="sidenav-footer">
    <div class="account-menu">
      @if (currentUser().firebaseUser) {
        @if (currentUser().selectedAccount) {
          <button
            type="button"
            #accountTrigger
            (click)="toggleAccountMenu($event)"
            class="account-trigger"
            [attr.aria-label]="userFullName || 'Account menu'"
            [attr.aria-expanded]="accountMenuOpen()"
            aria-haspopup="menu"
          >
            <osk-avatar
              size="md"
              color="primary"
              [initials]="userInitials"
              [src]="currentUser().firebaseUser?.photoURL || undefined"
            />
            @if (!isCollapsed) {
              <div class="user-details">
                <span class="user-name">{{
                  userFullName || currentUser().selectedAccount?.name
                }}</span>
                <span class="user-email">{{ userEmail }}</span>
              </div>
              <div class="user-menu-chevron">
                <osk-icon name="chevrons-up-down" [size]="16" />
              </div>
            }
          </button>

          @if (accountMenuOpen()) {
            <div class="user-account-menu-panel" role="menu">
              <div class="user-menu-header" role="presentation" (click)="$event.stopPropagation()">
                <osk-avatar
                  size="sm"
                  color="primary"
                  [initials]="userInitials"
                  [src]="currentUser().firebaseUser?.photoURL || undefined"
                />
                <div class="user-details">
                  <span class="user-name">{{
                    userFullName || currentUser().selectedAccount?.name
                  }}</span>
                  <span class="user-email">{{ userEmail }}</span>
                </div>
              </div>

              @if (accountsList.length > 0) {
                <div class="user-menu-divider" role="separator"></div>
                @for (account of accountsList; track account.routerLink) {
                  <button
                    type="button"
                    role="menuitem"
                    [routerLink]="account.routerLink"
                    (click)="closeAccountMenu()"
                    class="user-menu-item"
                    [class.active-account]="
                      currentUser().selectedAccount?.routerLink === account.routerLink
                    "
                  >
                    <osk-icon
                      [name]="$any(account.icon || 'shield')"
                      [size]="18"
                      class="user-menu-icon"
                      [class.account-selected]="
                        currentUser().selectedAccount?.routerLink === account.routerLink
                      "
                    ></osk-icon>
                    <span
                      class="user-menu-label"
                      [class.account-selected]="
                        currentUser().selectedAccount?.routerLink === account.routerLink
                      "
                    >
                      {{ account.name }}
                    </span>
                  </button>
                }
              }

              <div class="user-menu-divider" role="separator"></div>
              <button
                type="button"
                role="menuitem"
                (click)="signOut()"
                class="user-menu-item"
              >
                <osk-icon
                  [name]="$any('log-out')"
                  [size]="18"
                  class="user-menu-icon logout-btn"
                ></osk-icon>
                <span class="user-menu-label logout-btn">
                  {{ 'header.signOut' | oskTranslate | async }}
                </span>
              </button>
            </div>
          }
        }
      } @else if (currentUser().firebaseUser !== undefined) {
        <osk-button
          [link]="'/auth'"
          color="primary"
          appearance="soft"
          size="sm"
          [iconOnly]="true"
          leadingIcon="user"
          [ariaLabel]="('auth.signIn.signIn' | oskTranslate | async) ?? ''"
        />
      }
    </div>
  </div>
</div>
